import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Block, Close, Delete, Edit, InfoOutlined, RestoreFromTrash } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, forms, typography } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import {
  EntityAccountRow,
  PAYMENT_ORIGIN_TYPES,
  PaymentOriginRow,
  PaymentOriginType,
  paymentOriginsService,
} from "../services/paymentOriginsService";

/**
 * Financeiro → Formas de pagamento (RN-217). Duas coisas diferentes que se completam, na mesma
 * tela: a **forma de pagamento** é como o dinheiro entra ou sai (aparece no recebimento do paciente
 * e no livro-caixa) e a **conta bancária** é onde ele fica. Ligar uma à outra é o que permite ao
 * Fluxo de caixa e ao Cenário somar saldo por conta. O que já foi usado não se exclui — inativa.
 */

type TabIndex = 0 | 1;
type StatusFilter = "all" | "active" | "inactive";

interface OriginForm {
  id: string | null;
  name: string;
  type: PaymentOriginType;
  entityAccountId: string;
  active: boolean;
}

interface AccountForm {
  id: string | null;
  title: string;
  description: string;
  bankId: string;
  number: string;
  digit: string;
  pixKey: string;
  active: boolean;
}

// Rodapé de modal do design system: separado por borda, fundo claro
const dialogActionsSx = {
  padding: "1.5rem 2rem",
  borderTop: `1px solid ${colors.backgroundAlt}`,
  backgroundColor: colors.background,
  gap: "1rem",
};

const tabsSx = {
  borderBottom: "1px solid #e9ecef",
  minHeight: 44,
  marginBottom: "1rem",
  "& .MuiTab-root": { textTransform: "none", minHeight: 44, fontSize: "0.9rem" },
  "& .Mui-selected": { color: `${colors.primary} !important` },
  "& .MuiTabs-indicator": { backgroundColor: colors.primary },
};

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const PaymentOrigins: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();

  const [tab, setTab] = useState<TabIndex>(0);
  const [origins, setOrigins] = useState<PaymentOriginRow[]>([]);
  const [accounts, setAccounts] = useState<EntityAccountRow[]>([]);
  const [banks, setBanks] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | PaymentOriginType>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);

  const [originForm, setOriginForm] = useState<OriginForm | null>(null);
  const [accountForm, setAccountForm] = useState<AccountForm | null>(null);
  const [deleteOrigin, setDeleteOrigin] = useState<PaymentOriginRow | null>(null);
  const [deleteAccount, setDeleteAccount] = useState<EntityAccountRow | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await paymentOriginsService.getAll();
      setOrigins(data.origins);
      setAccounts(data.accounts);
      setBanks(data.banks);
    } catch {
      showToast("Erro ao carregar as formas de pagamento.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Formas de pagamento";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setStatusFilter("active");
    setCurrentPage(1);
  };

  const changeTab = (value: TabIndex) => {
    setTab(value);
    clearFilters();
  };

  // Aba 0 = Contas bancárias (cadastra-se a conta antes da forma que aponta para ela); aba 1 = Formas
  const isOriginsTab = tab === 1;

  const hasFilterChanges = search !== "" || typeFilter !== "all" || statusFilter !== "active";

  const matchesStatus = (active: boolean) =>
    statusFilter === "all" || (statusFilter === "active" ? active : !active);

  const filteredOrigins = useMemo(() => {
    const term = normalize(search.trim());
    return origins
      .filter((o) => typeFilter === "all" || o.type === typeFilter)
      .filter((o) => matchesStatus(o.active))
      .filter((o) => !term || normalize(`${o.name} ${o.entityAccountName ?? ""}`).includes(term));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origins, search, typeFilter, statusFilter]);

  const filteredAccounts = useMemo(() => {
    const term = normalize(search.trim());
    return accounts
      .filter((a) => matchesStatus(a.active))
      .filter((a) => !term || normalize(`${a.title} ${a.bankName ?? ""} ${a.number ?? ""} ${a.pixKey ?? ""}`).includes(term));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, search, statusFilter]);

  const filtered: (PaymentOriginRow | EntityAccountRow)[] = isOriginsTab ? filteredOrigins : filteredAccounts;
  const total = isOriginsTab ? origins.length : accounts.length;
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const pagedOrigins = useMemo(
    () => filteredOrigins.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filteredOrigins, currentPage, itemsPerPage],
  );
  const pagedAccounts = useMemo(
    () => filteredAccounts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filteredAccounts, currentPage, itemsPerPage],
  );

  // O filtro pode deixar o usuário numa página que não existe mais
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const activeAccounts = accounts.filter((a) => a.active);

  const openCreateOrigin = () => setOriginForm({
    id: null,
    name: "",
    type: typeFilter === "all" ? "Entrada" : typeFilter,
    entityAccountId: "",
    active: true,
  });

  const openEditOrigin = (row: PaymentOriginRow) => setOriginForm({
    id: row.id,
    name: row.name,
    type: row.type,
    entityAccountId: row.entityAccountId ?? "",
    active: row.active,
  });

  const openCreateAccount = () => setAccountForm({
    id: null, title: "", description: "", bankId: "", number: "", digit: "", pixKey: "", active: true,
  });

  const openEditAccount = (row: EntityAccountRow) => setAccountForm({
    id: row.id,
    title: row.title,
    description: row.description ?? "",
    bankId: row.bankId ?? "",
    number: row.number ?? "",
    digit: row.digit ?? "",
    pixKey: row.pixKey ?? "",
    active: row.active,
  });

  const saveOrigin = async () => {
    if (!originForm) return;
    if (!originForm.name.trim()) { showToast("Informe o nome da forma de pagamento.", "error"); return; }
    setSaving(true);
    try {
      await paymentOriginsService.save({
        id: originForm.id,
        name: originForm.name.trim(),
        type: originForm.type,
        entityAccountId: originForm.entityAccountId || null,
        active: originForm.active,
      });
      showToast(originForm.id ? "Forma de pagamento atualizada." : "Forma de pagamento incluída.", "success");
      setOriginForm(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao salvar a forma de pagamento.", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveAccount = async () => {
    if (!accountForm) return;
    if (!accountForm.title.trim()) { showToast("Informe o nome da conta.", "error"); return; }
    setSaving(true);
    try {
      await paymentOriginsService.saveAccount({
        id: accountForm.id,
        title: accountForm.title.trim(),
        description: accountForm.description.trim() || null,
        bankId: accountForm.bankId || null,
        number: accountForm.number.trim() || null,
        digit: accountForm.digit.trim() || null,
        pixKey: accountForm.pixKey.trim() || null,
        active: accountForm.active,
      });
      showToast(accountForm.id ? "Conta atualizada." : "Conta incluída.", "success");
      setAccountForm(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao salvar a conta bancária.", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleOrigin = async (row: PaymentOriginRow) => {
    try {
      await paymentOriginsService.save({
        id: row.id, name: row.name, type: row.type, entityAccountId: row.entityAccountId, active: !row.active,
      });
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao alterar a forma de pagamento.", "error");
    }
  };

  const toggleAccount = async (row: EntityAccountRow) => {
    try {
      await paymentOriginsService.saveAccount({
        id: row.id, title: row.title, description: row.description, bankId: row.bankId,
        number: row.number, digit: row.digit, pixKey: row.pixKey, active: !row.active,
      });
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao alterar a conta bancária.", "error");
    }
  };

  const confirmDeleteOrigin = async () => {
    if (!deleteOrigin) return;
    setSaving(true);
    try {
      await paymentOriginsService.remove(deleteOrigin.id);
      showToast("Forma de pagamento excluída.", "success");
      setDeleteOrigin(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao excluir a forma de pagamento.", "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteAccount = async () => {
    if (!deleteAccount) return;
    setSaving(true);
    try {
      await paymentOriginsService.removeAccount(deleteAccount.id);
      showToast("Conta bancária excluída.", "success");
      setDeleteAccount(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao excluir a conta bancária.", "error");
    } finally {
      setSaving(false);
    }
  };

  const emptyMessage = () => {
    if (total === 0) {
      return isOriginsTab
        ? "Nenhuma forma de pagamento cadastrada — inclua a primeira no botão acima."
        : "Nenhuma conta bancária cadastrada — inclua a primeira no botão acima.";
    }
    return "Nenhum registro encontrado com os filtros aplicados.";
  };

  // Conta inativa já escolhida continua no campo, senão desapareceria ao editar a forma de pagamento
  const originFormAccounts = accounts.filter((a) => a.active || a.id === originForm?.entityAccountId);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Formas de pagamento</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Como o dinheiro entra e sai, e em qual conta da clínica ele fica.
              </Typography>
            </Box>
            <FaqButton />
          </Box>

          <div className="c4u-panel">
            <Tabs value={tab} onChange={(_, value) => changeTab(value as TabIndex)} sx={tabsSx}>
              <Tab label="Contas bancárias" />
              <Tab label="Formas de pagamento" />
            </Tabs>

            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Buscar"
                  placeholder={isOriginsTab ? "Nome ou conta ligada" : "Conta, banco, número ou PIX"}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  fullWidth
                  sx={searchFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 100 }}
                />
                {isOriginsTab && (
                  <TextField
                    select
                    size="small"
                    label="Tipo"
                    value={typeFilter}
                    onChange={(e) => { setTypeFilter(e.target.value as typeof typeFilter); setCurrentPage(1); }}
                    fullWidth
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    {PAYMENT_ORIGIN_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                )}
                <TextField
                  select
                  size="small"
                  label="Situação"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setCurrentPage(1); }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="active">Ativos</MenuItem>
                  <MenuItem value="inactive">Inativos</MenuItem>
                  <MenuItem value="all">Todos</MenuItem>
                </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <AddButton
                  onClick={isOriginsTab ? openCreateOrigin : openCreateAccount}
                  title={isOriginsTab ? "Nova forma de pagamento" : "Nova conta bancária"}
                />
              </div>
            </div>

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!loading && (
              <>
                <Box className="c4u-info-box">
                  <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                  <Typography>
                    {isOriginsTab
                      ? "Entrada aparece no recebimento do paciente e no livro-caixa; Saída, só no livro-caixa. Ligar a forma a uma conta é o que permite ao Fluxo de caixa somar saldo por conta. Com lançamento ou recibo, não se exclui, só inativa."
                      : "A conta bancária é onde o dinheiro fica: banco, número e chave PIX, usados nos recibos e no controle de saldo. Conta ligada a uma forma de pagamento não se exclui, só inativa."}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <ResultCount shown={filtered.length} total={total} />

                  {filtered.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                      {emptyMessage()}
                    </Typography>
                  ) : isOriginsTab ? (
                    <Box className="admin-plans-table">
                      <Box className="c4u-table__header">
                        <Box className="admin-plans-header-cell" sx={{ flex: "2 1 240px", minWidth: 200 }}>Forma de pagamento</Box>
                        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px" }}>Tipo</Box>
                        <Box className="admin-plans-header-cell c4u-col--sm" sx={{ flex: "1 1 180px", minWidth: 150 }}>Conta bancária</Box>
                        <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 110px" }}>Lançamentos</Box>
                        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px" }}>Situação</Box>
                        <Box className="admin-plans-header-cell no-print" sx={{ flex: "0 0 130px", justifyContent: "flex-end" }}>Ações</Box>
                      </Box>

                      <Box className="admin-plans-table-body">
                        {pagedOrigins.map((row) => (
                          <Box key={row.id} className="c4u-table__row">
                            <Box className="admin-plans-cell admin-plans-name" sx={{ flex: "2 1 240px", minWidth: 200 }} data-label="Forma de pagamento">
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{row.name}</Typography>
                            </Box>
                            <Box className="admin-plans-cell" sx={{ flex: "0 0 110px" }} data-label="Tipo">
                              <Chip
                                label={row.type}
                                size="small"
                                sx={{ fontWeight: 600, backgroundColor: row.type === "Entrada" ? "#e6f4ea" : "#fdecea", color: row.type === "Entrada" ? "#1e7e34" : "#a12622" }}
                              />
                            </Box>
                            <Box className="admin-plans-cell c4u-col--sm" sx={{ flex: "1 1 180px", minWidth: 150 }} data-label="Conta bancária">
                              <Typography sx={{ fontSize: 13 }}>{row.entityAccountName || "—"}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 110px" }} data-label="Lançamentos">
                              <Typography sx={{ fontSize: 13 }}>{row.usageCount}</Typography>
                            </Box>
                            <Box className="admin-plans-cell" sx={{ flex: "0 0 100px" }} data-label="Situação">
                              <Chip
                                label={row.active ? "Ativo" : "Inativo"}
                                size="small"
                                sx={{ color: "white", fontWeight: 600, backgroundColor: row.active ? colors.success : "#adb5bd" }}
                              />
                            </Box>
                            <Box className="admin-plans-cell admin-plans-actions no-print" sx={{ flex: "0 0 130px" }} data-label="Ações">
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
                                <Tooltip title="Editar forma de pagamento">
                                  <IconButton className="c4u-row-action" size="small" onClick={() => openEditOrigin(row)} sx={rowActionSx(colors.primary, colors.primaryHover)}>
                                    <Edit sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={row.active ? "Inativar (sai da escolha; o histórico fica)" : "Reativar"}>
                                  <IconButton
                                    className="c4u-row-action"
                                    size="small"
                                    onClick={() => toggleOrigin(row)}
                                    sx={rowActionSx(row.active ? "#fd7e14" : colors.success, row.active ? "#e8590c" : "#218838")}
                                  >
                                    {row.active ? <Block sx={{ fontSize: "1rem" }} /> : <RestoreFromTrash sx={{ fontSize: "1rem" }} />}
                                  </IconButton>
                                </Tooltip>
                                {row.usageCount === 0 && (
                                  <Tooltip title="Excluir (sem lançamentos)">
                                    <IconButton className="c4u-row-action" size="small" onClick={() => setDeleteOrigin(row)} sx={rowActionSx("#dc3545", "#c82333")}>
                                      <Delete sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Box className="admin-plans-table">
                      <Box className="c4u-table__header">
                        <Box className="admin-plans-header-cell" sx={{ flex: "2 1 220px", minWidth: 180 }}>Conta</Box>
                        <Box className="admin-plans-header-cell c4u-col--sm" sx={{ flex: "1 1 250px", minWidth: 200 }}>Banco</Box>
                        <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 130px" }}>Número</Box>
                        <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "1 1 170px", minWidth: 140 }}>Chave PIX</Box>
                        <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 80px" }}>Formas</Box>
                        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px" }}>Situação</Box>
                        <Box className="admin-plans-header-cell no-print" sx={{ flex: "0 0 130px", justifyContent: "flex-end" }}>Ações</Box>
                      </Box>

                      <Box className="admin-plans-table-body">
                        {pagedAccounts.map((row) => (
                          <Box key={row.id} className="c4u-table__row">
                            <Box className="admin-plans-cell admin-plans-name" sx={{ flex: "2 1 220px", minWidth: 180 }} data-label="Conta">
                              {/* a célula do padrão é flex em linha: sem o wrapper, a observação fica ao lado do nome */}
                              <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{row.title}</Typography>
                                {row.description && (
                                  <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>{row.description}</Typography>
                                )}
                              </Box>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--sm" sx={{ flex: "1 1 250px", minWidth: 200 }} data-label="Banco">
                              <Typography sx={{ fontSize: 13 }}>{row.bankName || "—"}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--xl" sx={{ flex: "0 0 130px" }} data-label="Número">
                              <Typography sx={{ fontSize: 13 }}>
                                {row.number ? `${row.number}${row.digit ? `-${row.digit}` : ""}` : "—"}
                              </Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--md" sx={{ flex: "1 1 170px", minWidth: 140 }} data-label="Chave PIX">
                              <Typography sx={{ fontSize: 13, wordBreak: "break-all" }}>{row.pixKey || "—"}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 80px" }} data-label="Formas">
                              <Typography sx={{ fontSize: 13 }}>{row.originsCount}</Typography>
                            </Box>
                            <Box className="admin-plans-cell" sx={{ flex: "0 0 100px" }} data-label="Situação">
                              <Chip
                                label={row.active ? "Ativa" : "Inativa"}
                                size="small"
                                sx={{ color: "white", fontWeight: 600, backgroundColor: row.active ? colors.success : "#adb5bd" }}
                              />
                            </Box>
                            <Box className="admin-plans-cell admin-plans-actions no-print" sx={{ flex: "0 0 130px" }} data-label="Ações">
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
                                <Tooltip title="Editar conta">
                                  <IconButton className="c4u-row-action" size="small" onClick={() => openEditAccount(row)} sx={rowActionSx(colors.primary, colors.primaryHover)}>
                                    <Edit sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={row.active ? "Inativar conta" : "Reativar conta"}>
                                  <IconButton
                                    className="c4u-row-action"
                                    size="small"
                                    onClick={() => toggleAccount(row)}
                                    sx={rowActionSx(row.active ? "#fd7e14" : colors.success, row.active ? "#e8590c" : "#218838")}
                                  >
                                    {row.active ? <Block sx={{ fontSize: "1rem" }} /> : <RestoreFromTrash sx={{ fontSize: "1rem" }} />}
                                  </IconButton>
                                </Tooltip>
                                {row.originsCount === 0 && (
                                  <Tooltip title="Excluir (sem forma de pagamento ligada)">
                                    <IconButton className="c4u-row-action" size="small" onClick={() => setDeleteAccount(row)} sx={rowActionSx("#dc3545", "#c82333")}>
                                      <Delete sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {filtered.length > 0 && (
                    <StandardPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      itemsPerPage={itemsPerPage}
                      totalItems={filtered.length}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                    />
                  )}
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <Dialog open={originForm !== null} onClose={() => setOriginForm(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {originForm?.id ? "Editar forma de pagamento" : "Nova forma de pagamento"}
          </Typography>
          <IconButton onClick={() => setOriginForm(null)} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {originForm && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: forms.fieldGapX, rowGap: forms.fieldGapY, alignItems: "flex-start" }}>
              <TextField
                label="Nome"
                value={originForm.name}
                onChange={(e) => setOriginForm({ ...originForm, name: e.target.value })}
                size="small"
                fullWidth
                required
                placeholder="Ex.: Itaú - PIX"
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { sm: "1 / -1" } }}
              />
              <TextField
                select
                label="Tipo"
                value={originForm.type}
                onChange={(e) => setOriginForm({ ...originForm, type: e.target.value as PaymentOriginType })}
                size="small"
                fullWidth
                required
                helperText="Entrada aparece no recebimento do paciente"
                InputLabelProps={{ shrink: true }}
              >
                {PAYMENT_ORIGIN_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField
                select
                label="Conta bancária"
                value={originForm.entityAccountId}
                onChange={(e) => setOriginForm({ ...originForm, entityAccountId: e.target.value })}
                size="small"
                fullWidth
                helperText={activeAccounts.length === 0 ? "Cadastre a conta na aba Contas bancárias" : "Opcional"}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Sem conta ligada</MenuItem>
                {originFormAccounts.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.active ? a.title : `${a.title} (inativa)`}</MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={<Switch checked={originForm.active} onChange={(e) => setOriginForm({ ...originForm, active: e.target.checked })} />}
                label={originForm.active ? "Ativa" : "Inativa"}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setOriginForm(null)} variant="outlined" disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button
            onClick={saveOrigin}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={accountForm !== null} onClose={() => setAccountForm(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {accountForm?.id ? "Editar conta bancária" : "Nova conta bancária"}
          </Typography>
          <IconButton onClick={() => setAccountForm(null)} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {accountForm && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: forms.fieldGapX, rowGap: forms.fieldGapY, alignItems: "flex-start" }}>
              <TextField
                label="Nome da conta"
                value={accountForm.title}
                onChange={(e) => setAccountForm({ ...accountForm, title: e.target.value })}
                size="small"
                fullWidth
                required
                placeholder="Ex.: Itaú - Conta Jurídica"
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { sm: "1 / -1" } }}
              />
              <TextField
                select
                label="Banco"
                value={accountForm.bankId}
                onChange={(e) => setAccountForm({ ...accountForm, bankId: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ gridColumn: { sm: "1 / -1" } }}
              >
                <MenuItem value="">Não informado</MenuItem>
                {banks.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              </TextField>
              <TextField
                label="Conta"
                value={accountForm.number}
                onChange={(e) => setAccountForm({ ...accountForm, number: e.target.value })}
                size="small"
                fullWidth
                placeholder="Sem o dígito"
                inputProps={{ maxLength: 30 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Dígito"
                value={accountForm.digit}
                onChange={(e) => setAccountForm({ ...accountForm, digit: e.target.value })}
                size="small"
                fullWidth
                inputProps={{ maxLength: 5 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Chave PIX"
                value={accountForm.pixKey}
                onChange={(e) => setAccountForm({ ...accountForm, pixKey: e.target.value })}
                size="small"
                fullWidth
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { sm: "1 / -1" } }}
              />
              <TextField
                label="Observação"
                value={accountForm.description}
                onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
                size="small"
                fullWidth
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { sm: "1 / -1" } }}
              />
              <FormControlLabel
                control={<Switch checked={accountForm.active} onChange={(e) => setAccountForm({ ...accountForm, active: e.target.checked })} />}
                label={accountForm.active ? "Ativa" : "Inativa"}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setAccountForm(null)} variant="outlined" disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button
            onClick={saveAccount}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteOrigin !== null}
        title="Excluir forma de pagamento"
        message={`Excluir "${deleteOrigin?.name ?? ""}"? Só é possível porque nenhum lançamento ou recibo a usa.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDeleteOrigin}
        onCancel={() => setDeleteOrigin(null)}
      />

      <ConfirmModal
        isOpen={deleteAccount !== null}
        title="Excluir conta bancária"
        message={`Excluir "${deleteAccount?.title ?? ""}"? Só é possível porque nenhuma forma de pagamento está ligada a ela.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDeleteAccount}
        onCancel={() => setDeleteAccount(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default PaymentOrigins;
